import { CalendarDays, FileStack, ShoppingBag, IndianRupee, Percent, Wallet } from 'lucide-react';
import React from 'react';

import { KpiGrid } from '../KpiCard';
import { ReportKpiCard } from '../report-cards';
import { LegacyTable, ReportSection } from '../ReportSection';

import { inr, PipelineLeadsTable, ratio } from './band-helpers';

import type { BandProps } from './band-props';

/**
 * The Pipeline band, split so the headline summary sits high in the dashboard
 * flow (position 2) while the heavier detail tables sit lower (position 8) -
 * pure relocation, no data/logic change. `variant="summary"` = the Pipeline
 * KPI strip (incl. Today's Sale) + Today's Pipeline table; `variant="detail"`
 * = the MTD Pipeline + Realised Pipeline tables. Both read the SAME props.
 */
export const PipelineBand = ({
  variant,
  loading,
  dash,
  expandAllAction,
  todaysPipelineData,
  todaysPipelineLoading,
  expectedPipelineData,
  expectedPipelineLoading,
  scopedRow,
  todayPipeRows,
  mtdPipeRows,
  realisedCount,
  sumPipeTtv,
  realisedRows,
  realisedTotalRow,
}: BandProps & { variant: 'summary' | 'detail' }): React.JSX.Element => {
  if (variant === 'detail') {
    return (
      <div className="space-y-4 pt-2">
        <PipelineLeadsTable
          title="MTD Pipeline"
          pipeline={expectedPipelineData}
          loading={expectedPipelineLoading}
          withTotals
        />

        <ReportSection title="Realised Pipeline" actions={expandAllAction}>
          <LegacyTable
            loading={loading || expectedPipelineLoading || !expectedPipelineData}
            headers={[
              'Agent',
              'No. of pipeline files',
              'Count of realised pipeline',
              'Realised %',
              'TTV',
              'Realised TTV',
            ]}
            leadRow={realisedTotalRow}
            rows={realisedRows}
          />
        </ReportSection>
      </div>
    );
  }
  return (
    <div className="space-y-4 pt-2">
      <KpiGrid cols={6}>
        <ReportKpiCard
          icon={ShoppingBag}
          tone="success"
          value={scopedRow ? inr(scopedRow.todaySaleAmount ?? 0) : dash}
          label="Today's Sale"
        />
        <ReportKpiCard
          icon={FileStack}
          tone="primary"
          value={todaysPipelineData ? todayPipeRows.length : dash}
          label="Today's Pipeline Files"
        />
        <ReportKpiCard
          icon={IndianRupee}
          value={todaysPipelineData ? inr(sumPipeTtv(todayPipeRows)) : dash}
          label="Today's Pipeline TTV"
        />
        <ReportKpiCard
          icon={CalendarDays}
          tone="primary"
          value={expectedPipelineData ? mtdPipeRows.length : dash}
          label="MTD Pipeline Files"
        />
        <ReportKpiCard
          icon={Wallet}
          value={expectedPipelineData ? inr(sumPipeTtv(mtdPipeRows)) : dash}
          label="MTD Pipeline TTV"
        />
        <ReportKpiCard
          icon={Percent}
          tone="success"
          value={expectedPipelineData ? ratio(realisedCount, mtdPipeRows.length) : dash}
          label="Realised %"
          hint={
            expectedPipelineData ? `${realisedCount} of ${mtdPipeRows.length} files` : undefined
          }
        />
      </KpiGrid>

      <PipelineLeadsTable
        title="Today's Pipeline"
        pipeline={todaysPipelineData}
        loading={todaysPipelineLoading}
        compact
      />
    </div>
  );
};
